"use client";

import { useSession } from "next-auth/react";
import config from "@/config";
import { useAuthModal } from "@/contexts/AuthModalContext";
import { useCheckoutLoading } from "@/contexts/CheckoutLoadingContext";
import {
  buildAuthCallbackUrl,
  PRICING_FALLBACK_URL,
  resumePendingCheckout,
  setPendingCheckout,
} from "@/libs/pendingCheckout";
import { redirectTo } from "@/libs/redirectLoading";
import { startStripeCheckout } from "@/libs/stripeCheckout";
import { trackFunnelEvent } from "@/libs/ga";

// Unified component for both subscription plans and credit packages
// Handles Stripe Checkout Sessions for payments and subscriptions
const ButtonCheckout = ({
  priceId,
  mode = "payment",
  text,
  className,
  quantity,
  couponId,
  credits,
  showIcon = false,
  purchaseTrigger = "voluntary",
}: {
  priceId: string;
  mode?: "payment" | "subscription";
  text?: string;
  className?: string;
  quantity?: number;
  couponId?: string;
  credits?: number;
  showIcon?: boolean;
  purchaseTrigger?: "voluntary" | "forced";
}) => {
  const { status } = useSession();
  const { openAuthModal } = useAuthModal();
  const { loadingPriceId, isCheckoutBusy, beginStripeCheckout, endStripeCheckout } =
    useCheckoutLoading();

  const isThisButtonLoading = loadingPriceId === priceId;
  const isDisabled = isCheckoutBusy;

  const handlePayment = async () => {
    if (isDisabled) return;

    trackFunnelEvent("monetization", mode === "subscription" ? "plan_selected" : "credit_package_selected", {
      price_id: priceId,
      mode,
      credits,
      quantity: mode === "subscription" ? 1 : quantity,
      purchase_trigger: purchaseTrigger,
      package_type: mode === "subscription" ? "subscription" : "topup",
    });

    if (status !== "authenticated") {
      const pathname =
        typeof window !== "undefined" ? window.location.pathname : "/";
      const search =
        typeof window !== "undefined" ? window.location.search : "";

      const intent = {
        priceId,
        mode,
        credits,
        quantity: mode === "subscription" ? 1 : quantity,
        couponId,
        purchaseTrigger,
        resumeAfterAuth: true,
      };

      // In-memory + sessionStorage + URL params (email magic-link fallback).
      setPendingCheckout(intent);

      openAuthModal({
        callbackUrl: buildAuthCallbackUrl(pathname, search, intent),
        purchaseContext: {
          priceId,
          credits,
          mode,
        },
        onSuccess: () => {
          beginStripeCheckout(priceId);
          void resumePendingCheckout({
            waitForSession: true,
            skipRedirectOverlay: true,
          }).then((ok) => {
            if (!ok) {
              endStripeCheckout();
              redirectTo(PRICING_FALLBACK_URL);
            }
          });
        },
      });
      return;
    }

    beginStripeCheckout(priceId);
    const ok = await startStripeCheckout({
      priceId,
      mode,
      quantity,
      couponId,
      credits,
      purchaseTrigger,
      skipRedirectOverlay: true,
    });
    if (!ok) {
      endStripeCheckout();
    }
  };

  // Generate default text based on context
  const getButtonText = () => {
    if (isThisButtonLoading) return "Redirecting to checkout…";
    if (text) return text;
    if (credits) return `Buy ${credits} Credits`;
    return `Get ${config?.appName}`;
  };

  return (
    <button
      className={className || "btn btn-primary btn-block group text-xl"}
      onClick={() => handlePayment()}
      disabled={isDisabled}
      aria-busy={isThisButtonLoading}
    >
      {isThisButtonLoading ? (
        <span className="inline-flex items-center justify-center gap-2">
          <span className="loading loading-spinner loading-sm" />
          {getButtonText()}
        </span>
      ) : (
        <>
          {showIcon && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5 mr-2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h-6.75"
              />
            </svg>
          )}
          {getButtonText()}
        </>
      )}
    </button>
  );
};

export default ButtonCheckout;
